import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Pause, Calendar, Clock, Headphones, ArrowRight, Star, Loader2 } from 'lucide-react';
import usePodcasts, { type PodcastWithRelations } from '../hooks/usePodcasts';
import { useAudioPlayer } from './Podcasts/hooks/useAudioPlayer';
import { AudioPlayer } from './Podcasts/components/AudioPlayer';
import { PlayButton } from './Podcasts/components/PlayButton';

const LatestEpisodes = () => {
  const [podcasts, setPodcasts] = useState<PodcastWithRelations[]>([]);
  const { loading: podcastsLoading, fetchPodcasts } = usePodcasts();
  const { 
    currentPodcast,
    isPlaying,
    loadingAudio,
    handlePlayPause,
    audioControls
  } = useAudioPlayer();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPodcasts();
  }, []);

  const loadPodcasts = async () => {
    try {
      const data = await fetchPodcasts();
      setPodcasts(data);
    } catch (error) {
      console.error('Error loading podcasts:', error);
    }
  };

  if (podcastsLoading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Get latest episodes (sorted by publish date)
  const latestEpisodes = [...podcasts]
    .sort((a, b) => new Date(b.published_at || '').getTime() - new Date(a.published_at || '').getTime())
    .slice(0, 5); // Get top 5 latest episodes

  const [featuredEpisode, ...recentEpisodes] = latestEpisodes;

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-background to-secondary/20">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Featured Episode */}
        {featuredEpisode && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              Episódio em Destaque
            </h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative h-[500px] rounded-2xl overflow-hidden group"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url(${featuredEpisode.image_url})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />

              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex items-center gap-2 px-3 py-1 bg-primary text-primary-foreground rounded-full text-sm">
                      <Star className="w-4 h-4" />
                      Novo Episódio
                    </span>
                    <span className="px-3 py-1 bg-secondary/20 backdrop-blur-sm rounded-full text-sm">
                      {featuredEpisode.category?.name}
                    </span>
                  </div>

                  <h2 className="text-4xl font-bold mb-4 group-hover:text-primary transition-colors max-w-2xl">
                    {featuredEpisode.title}
                  </h2>

                  <p className="text-gray-300 mb-8 line-clamp-3 max-w-2xl text-lg">
                    {featuredEpisode.description}
                  </p>

                  <div className="flex items-center gap-8">
                    <button
                      onClick={() => handlePlayPause(featuredEpisode)}
                      disabled={loadingAudio && currentPodcast?.id !== featuredEpisode.id}
                      className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-blue-600 text-primary-foreground rounded-full hover:from-blue-600 hover:to-primary transition-all duration-300 hover:scale-105 disabled:opacity-50 text-lg font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 border border-primary/20"
                    >
                      {loadingAudio && currentPodcast?.id === featuredEpisode.id ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          Carregando...
                        </>
                      ) : currentPodcast?.id === featuredEpisode.id && isPlaying ? (
                        <>
                          <Pause className="w-6 h-6" />
                          Pausar Episódio
                        </>
                      ) : (
                        <>
                          <Play className="w-6 h-6 drop-shadow-[0_0_5px_rgba(0,149,255,0.5)]" />
                          <span className="relative">
                            <span className="whitespace-nowrap">Ouvir Episódio</span>
                            <motion.span 
                              className="absolute -bottom-1 left-0 right-0 h-0.5 bg-white/70"
                              initial={{ width: 0 }}
                              animate={{ width: "100%" }}
                              transition={{ duration: 0.3, delay: 0.2 }}
                            />
                          </span>
                        </>
                      )}
                    </button>

                    <div className="flex items-center gap-8 text-base text-gray-300">
                      <span className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        {new Date(featuredEpisode.published_at || '').toLocaleDateString('pt-BR')}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        {featuredEpisode.duration}
                      </span>
                      <span className="flex items-center gap-2">
                        <Headphones className="w-5 h-5" />
                        {featuredEpisode.plays} plays
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Recent Episodes */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-8">Episódios Recentes</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {recentEpisodes.map((episode, index) => (
              <motion.div
                key={episode.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-lg overflow-hidden hover:ring-1 hover:ring-primary/50 transition-all group"
              >
                <div
                  className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url(${episode.image_url})` }}
                />
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <span className="px-3 py-1 bg-secondary rounded-full text-sm">
                      {episode.category?.name}
                    </span>
                  </div>

                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {episode.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {episode.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handlePlayPause(episode)}
                      disabled={loadingAudio && currentPodcast?.id !== episode.id} 
                      className="flex items-center gap-2 flex-grow md:flex-grow-0"
                    >
                      <PlayButton
                        podcast={episode}
                        currentPodcast={currentPodcast}
                        isPlaying={isPlaying}
                        loadingAudio={loadingAudio}
                        onPlayPause={handlePlayPause}
                        size="sm"
                        variant="full"
                      />
                    </button>
                    <div className="hidden md:flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {episode.duration}
                      </span>
                      <span className="flex items-center gap-2">
                        <Headphones className="w-4 h-4" />
                        {episode.plays} plays
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <Link
            to="/podcasts"
            className="inline-flex items-center gap-2 px-8 py-3 bg-secondary hover:bg-secondary/80 rounded-full transition-colors group"
          >
            Ver Todos os Episódios
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Global Audio Player */}
        {currentPodcast && (
          <AudioPlayer
            podcast={currentPodcast}
            isPlaying={isPlaying}
            controls={audioControls}
            onPlayPause={handlePlayPause}
          />
        )}
      </div>
    </div>
  );
};

export default LatestEpisodes;